import React, { FC, useEffect, useState } from 'react';
import { Select, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EmptyState, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { ERoute } from 'common/const';
import { getFilterOptions } from 'common/helpers';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_LIST_OFFSET,
  DEFAULT_PAGINATION_PAGE,
  LIST_LIMIT_20,
  SELECT_LIST_HEIGHT_320,
} from 'common/config';
import { ArrowDownShort } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IBasketListParams } from 'entities/Basket/Basket.models';
import { renderActiveBasketListRecords } from 'entities/Basket/Basket.helper';
import {
  getBasketList,
  getBasketListChunk,
  getBasketSubdivisions,
  selectBasketCount,
  selectBasketList,
  selectBasketSubdivisions,
  selectIsBasketLoading,
} from 'entities/Basket/Basket.slice';
import { getBuyerList, selectBuyerList } from 'entities/Buyer/Buyer.slice';

export const ActiveBasketListPage: FC = () => {
  const dispatch = useAppDispatch();
  // basket
  const basketList = useAppSelector(selectBasketList);
  const basketCount = useAppSelector(selectBasketCount);
  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  const basketSubdivisions = useAppSelector(selectBasketSubdivisions);
  // buyer
  const buyerList = useAppSelector(selectBuyerList);

  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [params, setParams] = useState<IBasketListParams>({
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const navigate = useNavigate();

  const buyerOptions = getFilterOptions.buyer(buyerList);
  const subdivisionOptions = getFilterOptions.subdivision(basketSubdivisions);

  const onBuyerChange = async (value: number) => {
    const newParams = {
      ...params,
      accountId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
      subdivisionId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setParams(newParams);
    await dispatch(getBasketSubdivisions({ accountId: newParams.accountId }));
    await dispatch(getBasketList(newParams));
  };

  const onSubdivisionChange = (value: number) => {
    const newParams = { ...params, subdivisionId: value === DEFAULT_EMPTY_VALUE ? undefined : value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setParams(newParams);
    dispatch(getBasketList(newParams));
  };

  const onSearchClick = async (value?: string) => {
    const newParams = { ...params, search: value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setParams(newParams);
    await dispatch(getBasketList(newParams));
  };

  const navigateToBasket = (id: number) => {
    navigate(`${ERoute.ActiveBasketList}/${id}`);
  };

  useEffect(() => {
    const fetch = async () => {
      await dispatch(getBuyerList());
      await dispatch(getBasketList(params));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <div className="redesign relative h-full flex direction-column active-basket-list">
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isBasketLoading && basketList.length < basketCount}
        loader={<Spin />}
        scrollToTopTrigger={[params]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getBasketListChunk({
              ...params,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        <div className="mb-6 ff-regular fs-m color-dark-grey">Корзины клиентов</div>

        <div className="mb-14 ff-regular fs-xxl">Список активных корзин</div>

        <div className="mb-6 flex align-center">
          <div className="mr-4 w-full active-basket-list__filters-selector">
            <Select
              rootClassName="redesign"
              options={buyerOptions}
              value={params.accountId ? params.accountId : DEFAULT_EMPTY_VALUE}
              onChange={onBuyerChange}
              placeholder="Выберите компанию"
              listHeight={SELECT_LIST_HEIGHT_320}
              virtual={false}
              suffixIcon={<ArrowDownShort />}
            />
          </div>

          {params.accountId && (
            <div className="mr-4 w-full active-basket-list__filters-selector">
              <Select
                rootClassName="redesign"
                options={subdivisionOptions}
                value={params.subdivisionId ? params.subdivisionId : DEFAULT_EMPTY_VALUE}
                onChange={onSubdivisionChange}
                placeholder="Выберите подразделение"
                listHeight={SELECT_LIST_HEIGHT_320}
                virtual={false}
                suffixIcon={<ArrowDownShort />}
              />
            </div>
          )}

          <SearchInput placeholder="Введите почту, номер корзины или ФИО пользователя" onSearch={onSearchClick} />
        </div>

        <Table
          className={`mb-11 table-row-middle ${!!basketList.length && basketList.length === basketCount && 'table-end-of-list'} ${!basketList.length && 'table-empty'}`}
          dataSource={basketList.map((basket) => ({ ...basket, key: basket.id }))}
          tableLayout="auto"
          columns={renderActiveBasketListRecords(navigateToBasket)}
          showHeader={false}
          pagination={false}
          locale={{
            emptyText: fetchLoading ? (
              <Spin />
            ) : (
              <EmptyState
                title="Корзины клиентов не найдены"
                description="Корзины клиентов появятся здесь при наличии активных контрактов. Вы сможете добавлять товары в них напрямую из
                      каталога."
              />
            ),
          }}
        />
      </InfiniteScroll>
    </div>
  );
};
